"use client";

import { Product } from "@/lib/types";
import { useCartStore } from "@/store/cart";
import Image from "next/image";
import Link from "next/link";
import React, { MouseEvent, useEffect } from "react";
import { Button } from "./ui/button";
import { PLACEHOLDER_IMAGE } from "@/lib/constant";
import { useAuthStore } from "@/store/auth";
import { useHasHydrated } from "@/hooks/use-hydration";

type ProductCardProps = {
  product: Product;
};

function ProductCard({ product }: ProductCardProps) {
  const { token } = useAuthStore();
  const { addItem, setIsLoggedIn, setToken } = useCartStore();
  const authHydrated = useHasHydrated();

  useEffect(() => {
    if (!authHydrated) return;

    setToken(token);
    if (token) setIsLoggedIn(true);
    else setIsLoggedIn(false);
  }, [authHydrated, setIsLoggedIn, setToken, token]);

  function handleAddToCart(
    event: MouseEvent<HTMLButtonElement>,
    product: Product,
  ) {
    event.preventDefault();
    addItem({
      item_id: product.id,
      name: product.name,
      quantity: 1,
      price: product.price,
      image: product.image,
    });
  }

  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden group transform hover:-translate-y-2 transition-all duration-300">
      <div className="relative">
        <Image
          src={product.image || PLACEHOLDER_IMAGE}
          alt={product.name}
          className="w-full h-64 object-cover"
          width={400}
          height={400}
        />
        <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-100 transition-all duration-300 flex items-center justify-center">
          <Link
            href={`/products/${product.id}`}
            className="text-white bg-green-600/80 px-6 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:cursor-pointer"
          >
            View Details
          </Link>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-800 truncate cursor-pointer">
          <Link href={`/products/${product.id}`}>{product.name}</Link>
        </h3>
        <p className="text-green-800 font-semibold mt-1">
          ₹{product.price.toFixed(2)}
        </p>
        <Button
          className="w-full mt-4 bg-green-500 text-white font-semibold py-2 rounded-lg hover:bg-green-600 transition duration-300 cursor-pointer"
          onClick={(e) => handleAddToCart(e, product)}
        >
          Add to Cart
        </Button>
      </div>
    </div>
  );
}

export default ProductCard;
